// SPDX-FileCopyrightText: Copyright 2023 Dash0 Inc.

import { Slot } from "@radix-ui/react-slot";
import { CSSProperties, PropsWithChildren } from "react";

declare module "react" {
	// Extend the CSSProperties interface to include our custom properties for inset padding.
	interface CSSProperties {
		"--inset-padding-top"?: string;
		"--inset-padding-bottom"?: string;
		"--inset-padding-left"?: string;
		"--inset-padding-right"?: string;
	}
}

export function getCustomPropertiesForInsetSupport(
	paddingTop: string,
	paddingBottom: string,
	paddingLeft: string,
	paddingRight: string
): CSSProperties {
	return {
		[paddingTopVar]: paddingTop,
		[paddingBottomVar]: paddingBottom,
		[paddingLeftVar]: paddingLeft,
		[paddingRightVar]: paddingRight,
	};
}

export const paddingTopVar = "--inset-padding-top";
export const paddingLeftVar = "--inset-padding-left";
export const paddingRightVar = "--inset-padding-right";
export const paddingBottomVar = "--inset-padding-bottom";

type Side = "all" | "x" | "y" | "top" | "bottom" | "left" | "right" | "without-top";

export interface InsetProps extends PropsWithChildren {
	side?: Side;
	fullHeight?: boolean;
	/**
	 * Re-applies the external padding internally. This is useful for cases in which you
	 * want to have links or buttons to span the full area, while visually looking like
	 * there is no inset.
	 */
	reapplyExternalPaddingInternally?: boolean;
	className?: string;
	style?: CSSProperties;
	asChild?: boolean;
}

/*
This component is based on the idea of https://www.radix-ui.com/themes/docs/components/inset
It enhances this idea by allowing different insets to the respective sides
 */
export function Inset({
	side = "all",
	fullHeight = false,
	className,
	style: externalStyle,
	reapplyExternalPaddingInternally,
	asChild,
	...props
}: Readonly<InsetProps>) {
	const style = computeInsetStyle({ side, fullHeight, reapplyExternalPaddingInternally, style: externalStyle });
	const Comp = asChild ? Slot : "div";
	return <Comp className={className} style={style} {...props} />;
}

export const computeInsetStyle = ({
	side,
	fullHeight,
	reapplyExternalPaddingInternally,
	style: externalStyle,
}: InsetProps): CSSProperties => {
	let style: CSSProperties = externalStyle ? { ...externalStyle } : {};

	if (side === "all" || side === "x" || side === "left" || side === "without-top") {
		style.marginLeft = `calc(var(${paddingLeftVar}) * -1)`;

		if (reapplyExternalPaddingInternally) {
			style.paddingLeft = `var(${paddingLeftVar})`;
		}
	}

	if (side === "all" || side === "x" || side === "right" || side === "without-top") {
		style.marginRight = `calc(var(${paddingRightVar}) * -1)`;

		if (reapplyExternalPaddingInternally) {
			style.paddingRight = `var(${paddingRightVar})`;
		}
	}

	if (side === "all" || side === "y" || side === "top") {
		style.marginTop = `calc(var(${paddingTopVar}) * -1)`;

		if (reapplyExternalPaddingInternally) {
			style.paddingTop = `var(${paddingTopVar})`;
		}
	}

	if (side === "all" || side === "y" || side === "bottom" || side === "without-top") {
		style.marginBottom = `calc(var(${paddingBottomVar}) * -1)`;

		if (reapplyExternalPaddingInternally) {
			style.paddingBottom = `var(${paddingBottomVar})`;
		}
	}

	if (fullHeight) {
		let heightCalculation = "100%";
		if (side === "all" || side === "y" || side === "top") {
			heightCalculation += ` + var(${paddingTopVar})`;
		}
		if (side === "all" || side === "y" || side === "bottom") {
			heightCalculation += ` + var(${paddingBottomVar})`;
		}

		style.height = `calc(${heightCalculation})`;
	}
	return style;
};
